import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, UserCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';
const STORAGE_KEY = 'tenantImpersonation';

// Sticky banner shown on every portal page while an operator is viewing
// a tenant's account. High-contrast on purpose -- the operator must
// never forget they are acting on someone else's behalf.
//
// Reads its session state out of localStorage (set by the CustomerForm
// "View as tenant" handler). On exit it posts to the backend, clears
// state, and routes the operator back to the customer detail page.
const TenantImpersonationBanner = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_err) {
      return null;
    }
  });
  const [now, setNow] = useState(Date.now());
  const [exiting, setExiting] = useState(false);

  // 1-second tick so the countdown stays live without thrashing renders
  // elsewhere on the page.
  useEffect(() => {
    if (!session) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [session]);

  const expiresAtMs = useMemo(() => {
    if (!session?.expires_at) return null;
    const t = Date.parse(session.expires_at);
    return Number.isNaN(t) ? null : t;
  }, [session]);

  const remainingMs = expiresAtMs ? Math.max(0, expiresAtMs - now) : 0;

  const handleExit = useCallback(async () => {
    if (!session) return;
    setExiting(true);
    try {
      // Operator's own access token is what authenticates the exit --
      // the impersonation token only authorizes tenant-facing routes.
      const operatorToken = session.operator_access_token
        || localStorage.getItem('accessToken');
      if (operatorToken && session.facility_id && session.session_id) {
        await axios.post(
          `${API_URL}/api/facilities/${session.facility_id}/impersonation/${session.session_id}/exit`,
          {},
          { headers: { Authorization: `Bearer ${operatorToken}` } },
        );
      }
    } catch (_err) {
      // Best-effort: the local cleanup below must happen regardless so
      // we never strand the operator in a "you're someone else" state.
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('tenantAccessToken');
      sessionStorage.removeItem('tenantAccessToken');
      setSession(null);
      setExiting(false);
      // Route back to the customer record the operator was viewing.
      if (session.customer_id) {
        navigate(`/customers/${session.customer_id}/edit`);
      } else {
        navigate('/customers');
      }
    }
  }, [navigate, session]);

  // Auto-clear once the token expires -- the backend will reject any
  // further requests anyway, but the banner should show the truth.
  useEffect(() => {
    if (!session || !expiresAtMs) return;
    if (remainingMs > 0) return;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('tenantAccessToken');
    sessionStorage.removeItem('tenantAccessToken');
    setSession(null);
  }, [expiresAtMs, remainingMs, session]);

  if (!session) return null;

  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const countdown = `${minutes}m ${String(seconds).padStart(2, '0')}s`;

  const tenantLabel = session.customer_name
    ? `${session.customer_name}${session.customer_email ? ` (${session.customer_email})` : ''}`
    : (session.customer_email || 'a tenant');

  return (
    <div className="sticky top-0 z-50 bg-rose-100 border-b border-rose-300 text-rose-900 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <UserCheck className="h-4 w-4 flex-shrink-0" />
          <p className="text-[13px] font-medium truncate">
            Acting as <span className="font-semibold">{tenantLabel}</span>
            {session.operator_email
              ? <> &middot; started by <span className="font-semibold">{session.operator_email}</span></>
              : null}
            {' '}&middot; expires in <span className="font-mono">{countdown}</span>
            {' '}&middot; every action is audit-logged
          </p>
        </div>
        <button
          type="button"
          onClick={handleExit}
          disabled={exiting}
          className="flex items-center gap-1.5 px-3 py-1 text-[12px] font-semibold bg-white text-rose-700 hover:bg-rose-50 border border-rose-300 rounded-md transition-colors disabled:opacity-50 flex-shrink-0"
        >
          <LogOut className="h-3 w-3" />
          {exiting ? 'Exiting...' : 'Exit impersonation'}
        </button>
      </div>
    </div>
  );
};

export default TenantImpersonationBanner;
